import { ButtonProps } from '@mui/material';
import type { Theme } from '@mui/material/styles';

import { makeStyles } from 'tss-react/mui';

function calculateBackgroundColor(
  variant: 'contained' | 'outlined' | 'text',
  color: ButtonProps['color'],
  theme: Theme,
) {
  if (variant === 'outlined' || variant === 'text') {
    return 'transparent';
  }

  if (color === 'error') {
    return theme.palette.error.main;
  }

  if (color === 'primary') {
    return (
      (theme.palette as { primaryGradient?: string }).primaryGradient ??
      theme.palette.primary.main
    );
  }

  if (color === 'secondary') {
    return theme.palette.secondary.main;
  }

  return theme.palette.primary.main;
}

function calculateTextColor(
  variant: 'contained' | 'outlined' | 'text',
  color: ButtonProps['color'],
  theme: Theme,
) {
  if (variant === 'contained' || variant === 'text') {
    if (color === 'error') {
      return theme.palette.error.contrastText;
    }

    if (color === 'secondary') {
      return theme.palette.secondary.contrastText;
    }

    if (color === 'primary') {
      return theme.palette.primary.contrastText;
    }

    return undefined;
  }

  if (color === 'primary') {
    return theme.palette.primary.main;
  }

  if (color === 'secondary') {
    return theme.palette.secondary.main;
  }

  if (color === 'error') {
    return theme.palette.error.main;
  }

  return undefined;
}

export const useStyles = makeStyles<{
  variant: 'contained' | 'outlined' | 'text';
  color?: ButtonProps['color'];
}>()((theme, { variant, color }) => {
  const backgroundColor = calculateBackgroundColor(variant, color, theme);
  const textColor = calculateTextColor(variant, color, theme);

  return {
    button: {
      borderRadius: '10px',
      textTransform: 'capitalize',
      transition: 'background-color 0.15s linear',

      background: backgroundColor ? `${backgroundColor} !important` : undefined,
      color: textColor ? `${textColor} !important` : undefined,
      '&.Mui-disabled': {
        color: `${theme.palette.action.disabled} !important`,
        background:
          variant === 'contained'
            ? `${theme.palette.action.disabledBackground} !important`
            : 'transparent !important',
        borderColor:
          variant === 'outlined'
            ? `${theme.palette.action.disabledBackground} !important`
            : undefined,
      },
      [theme.breakpoints.down('md')]: {
        fontSize: '1rem',
      },
    },
    circularProgress: {
      color: textColor,
    },
    labelWrap: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      minWidth: 0,
    },
  };
});
